import { ActionTreeWithRootState } from '~/store/types'
import {
  CLEAR_NOTIFICATION_CENTER_DATA,
  READ_ALL_CURRENT_NOTIFICATION_CENTER_ROWS,
  SET_USER_DATA,
  SET_USER_EXTRA_DATA,
  SET_USER_NOTIFICATION_CENTER_DATA,
  SET_USER_NOTIFICATION_CENTER_LOADING_STATE,
  MARK_AS_READ_NOTIFICATION,
  READ_ALL_NOTIFICATIONS
} from './mutation-types'
import { UserState } from './state'
import { SET_ADMIN_EXTRA_DATA } from '~/store/modules/shared/admin/user/mutation-types'
import { ADMINUSER_NS } from '~/store/modules/shared/admin/user/state'
import { UserType } from '~/models/user/types'
import NotificationService from '~/services/notification/NotificationService'
import { AxiosError } from 'axios'
import { NotificationEvent } from '~/models/notification/types'
import AuthUserService from '~/services/user/AuthUserService'
import UserExtrasService from '~/services/user/UserExtrasService'
import { HttpStatus } from '~/constants/http'
import { changeFaviconNotification } from '~/utils/dom'

export default {
  async loadUser({ dispatch }) {
    try {
      await Promise.all([
        dispatch('loadAuthUserData'),
        dispatch('loadUserExtras')
      ])
    } catch (error) {
      const { response } = error
      if (response) {
        return this.$error({ statusCode: error.response.status })
      }

      this.$logger.captureError(error as Error)
      return this.$error({ statusCode: HttpStatus.INTERNAL_SERVER_ERROR })
    }
  },
  async loadUserSubscribable({ dispatch }) {
    // used in cases where we want to run something after loadUser
    // like refreshing data without a window refresh
    // subscribe to this action instead of the simple loadUser (to avoid mishaps/bugs)
    await dispatch('loadUser')
  },
  async loadAuthUserData({ commit }) {
    const authUserService = this.$dep(AuthUserService)

    const userData = await authUserService.getUser()

    commit(SET_USER_DATA, userData)
    this.$logger.setUser(userData)
  },
  async loadUserExtras({ state, commit }) {
    const userExtrasService = this.$dep(UserExtrasService)

    const {
      userExtras,
      adminExtrasData
    } = await userExtrasService.getUserExtras()
    commit(SET_USER_EXTRA_DATA, userExtras)

    if (
      adminExtrasData &&
      ([UserType.ADMIN, undefined].includes(state.type) ||
        adminExtrasData.adminExtras.suas)
    ) {
      // undefined here cause there is a possible race condition with loadAuthUserData
      commit(
        `${ADMINUSER_NS}/${SET_ADMIN_EXTRA_DATA}`,
        adminExtrasData.adminExtras,
        {
          root: true
        }
      )
    }
  },
  async reloadUserExtras({ dispatch, getters }) {
    if (getters.isAdmin) {
      return
    }

    await dispatch('loadUserExtras')
  },
  async fetchNotificationCenterData(
    { commit },
    { page, group }: { page: number; group: string }
  ) {
    const notificationService = this.$dep(NotificationService)
    commit(SET_USER_NOTIFICATION_CENTER_LOADING_STATE, true)
    if ((page === 1 || !page) && !group) {
      commit(CLEAR_NOTIFICATION_CENTER_DATA)
    }
    try {
      const {
        notifications,
        unreadNotificationsCount,
        unseenNotificationsCount,
        groups: notificationGroups,
        pinnedAnnouncement
      } = await notificationService.fetchNotification(page, group)
      commit(SET_USER_NOTIFICATION_CENTER_DATA, {
        notifications,
        notificationGroups,
        unreadNotificationsCount,
        unseenNotificationsCount,
        pinnedAnnouncement
      })
    } catch (e) {
      notificationService.handleError(e as AxiosError)
    } finally {
      commit(SET_USER_NOTIFICATION_CENTER_LOADING_STATE, false)
    }
  },
  async readNotification({ state, commit }, notification: NotificationEvent) {
    const notificationService = this.$dep(NotificationService)
    try {
      await notificationService.readNotifications({
        notificationId: notification.id
      })
      commit(MARK_AS_READ_NOTIFICATION, notification.id)

      if (state.unreadNotifications === 0) {
        changeFaviconNotification(false)
      }
    } catch (e) {
      notificationService.handleError(e as AxiosError)
    }
  },
  async readAllNotifications({ state, commit }) {
    const notificationService = this.$dep(NotificationService)
    try {
      commit(SET_USER_NOTIFICATION_CENTER_LOADING_STATE, true)
      await notificationService.readNotifications({ all: true })

      await new Promise(resolve => setTimeout(resolve, 1000))

      if (state.unreadNotifications) {
        commit(READ_ALL_NOTIFICATIONS, 0)
      }

      commit(READ_ALL_CURRENT_NOTIFICATION_CENTER_ROWS)

      changeFaviconNotification(false)
      this.$snackbar.success(
        // @ts-ignore
        this.$i18n.t('success_clear_notifications')
      )
      return true
    } catch (e) {
      notificationService.handleError(e as AxiosError)
      return false
    } finally {
      commit(SET_USER_NOTIFICATION_CENTER_LOADING_STATE, false)
    }
  }
} as ActionTreeWithRootState<UserState>
